import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type { TEmitterType } from '@semios/app-platform-values-helpers-and-types'
import {
  getEmitterTypeToEnum,
  getEnumFromValueType,
  IRRIGATION_APPLIED_VOLUME_VALUE_TYPE_PREFIX,
} from '@semios/app-platform-values-helpers-and-types'
import { propertyLacksPermissionSectionMaker } from 'App/Map/PanelDetails/_utils/propertyLacksPermissionSectionMaker'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import type { TIrrigationDeviceStatus, TLngLat, TValuesCurrentPoints } from 'App/Map/types'
import type {
  GridTableContentSection,
  GridTableContentSectionItem,
  PossibleValuesRecord,
} from 'components/GridTable/types'
import { translate } from 'i18n/i18n'
import { isEmpty, isNil } from 'lodash'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TPoint } from 'stores/fieldAssetStore/helperTypes'
import type { TSelectedFieldAssetsStoreState } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import type { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { smallStore } from 'stores/smallStore/smallStore'
import { isPrePostFilterEmitterType } from 'utils/isPrePostFilterEmitterType'
import { minutesToHoursAndMinutes } from 'utils/minutesToHoursAndMinutes'
import { sortByKey } from 'utils/sortByKey'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { getRowHeightForLabelCell } from '../_utils/getRowHeightForLabelCell'
import { IrrigationActivityLabel } from '../_utils/IrrigationActivityLabel/IrrigationActivityLabel'
import { makeLabelForNameAndEmitterType } from '../_utils/makeLabelForNameAndEmitterType'
import { IrrigationActivityDropdown } from '../IrrigationActivityTitleBarChildren/IrrigationActivityDropdown'
import { IrrigationActivityToggleButton } from '../IrrigationActivityTitleBarChildren/IrrigationActivityToggleButton'

type CommonValueType = PossibleValuesRecord & { duration?: number | null; volume_mm?: number | null }

const checkPermission = () => selectedPropertyHasPermission({ permission: 'VIEW_IRRIGATION_DETAILS' })
const preferredAggregationInterval = { preferredAggregationInterval: 'DAILY' }

const isAppliedVolumeForNonFilterEmitter = (valueType: string) => {
  if (!valueType.includes(IRRIGATION_APPLIED_VOLUME_VALUE_TYPE_PREFIX)) return false

  const emitterType = getEnumFromValueType(valueType)

  return !!emitterType && !isPrePostFilterEmitterType(emitterType)
}

export const apiArgsForPressureSensors = ({
  selectedValueGroups,
  selectedFieldAssets,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}): Partial<routes.Values.Request> => {
  if (!selectedValueGroups.irrigation_activity_pressure || !selectedFieldAssets.property) return {}

  const property = fieldAssetStore.getState().properties?.[Number(selectedFieldAssets.property)]

  if (!property) return {}

  const lngLats: TLngLat[] = []
  const valuesRequested: Record<string, typeof preferredAggregationInterval> = {}

  const pointsWithPressure = Object.values(property.points ?? {}).filter((point) =>
    point.valuesTimeseries.some(isAppliedVolumeForNonFilterEmitter),
  )

  lngLats.push(...pointsWithPressure.map((point) => point.lngLat))

  pointsWithPressure.forEach((point) => {
    point.valuesTimeseries
      .filter(isAppliedVolumeForNonFilterEmitter)
      .forEach((vt) => (valuesRequested[vt] = preferredAggregationInterval))
  })

  if (isEmpty(lngLats) || isEmpty(valuesRequested)) return {}

  return {
    points: {
      lngLats,
      valuesRequested,
    },
  }
}

const generateItem = ({
  currentValuesData,
  deviceName,
  emitterType,
  externalVendorNames,
  id,
  lngLat,
  values,
}: {
  currentValuesData: routes.ValuesCurrent.Response['points']
  deviceName: string
  emitterType: TEmitterType
  externalVendorNames?: TPoint['externalVendorNames']
  id: string
  lngLat: string
  values: Record<string, CommonValueType>
}): GridTableContentSectionItem => {
  const label = makeLabelForNameAndEmitterType({
    deviceName,
    emitterType,
  })

  const height = getRowHeightForLabelCell({ label })
  const isDuration = smallStore.getState().appliedIrrigationActivityDisplay === 'DURATION'
  const currentValuesForPoint = currentValuesData?.[lngLat]

  let currentValueStatus: TIrrigationDeviceStatus | null = null

  if (currentValuesForPoint) {
    const currentValueKey = `irrigationIsOn_${getEmitterTypeToEnum(
      emitterType,
    )}_status` as TValuesCurrentPoints

    currentValueStatus = currentValuesForPoint[currentValueKey]?.value as TIrrigationDeviceStatus
  }

  const noValueDefaultReturn = translate.phrases.templates('-')

  return {
    id,
    label: (
      <IrrigationActivityLabel
        label={label}
        externalVendorNames={externalVendorNames}
        currentValueStatus={currentValueStatus}
        showPlanned={false}
      />
    ),
    labelString: label,
    height,
    labelMinWidth: 160,
    valueMinWidth: 120,
    render: (dataPoint: { duration: number; volume_mm: number }) => {
      if (!dataPoint) return noValueDefaultReturn

      const { duration, volume_mm } = dataPoint

      if (isDuration) {
        if (isNil(duration)) return noValueDefaultReturn

        const durationMinutes = duration ? duration / 60000 : 0

        return minutesToHoursAndMinutes(durationMinutes)
      } else {
        if (isNil(volume_mm)) return noValueDefaultReturn

        return unitConverter.irrigationActivity(volume_mm).valueWithSuffix()
      }
    },
    values,
  }
}

export const pressureSensorsActivityContent = ({
  data,
  selectedFieldAssets,
  currentValuesData,
}: {
  data: routes.Values.Response
  selectedFieldAssets: TSelectedFieldAssetsStoreState
  currentValuesData: routes.ValuesCurrent.Response
}): GridTableContentSection => {
  const title = translate.phrases.banyanApp('Irrigation Activity by Pressure Sensors')

  const commonReturnItems = {
    title,
    fieldAssetPicker: <IrrigationActivityDropdown />,
    id: 'summary-grid-pressureSensors',
  }

  if (!checkPermission()) return propertyLacksPermissionSectionMaker(commonReturnItems)

  const property = fieldAssetStore.getState().properties?.[Number(selectedFieldAssets.property)]
  const points: TPoint[] = Object.values(property?.points || {})

  const hasIrrigationAppliedVolume = points.some(({ valuesTimeseries }) =>
    valuesTimeseries.some(isAppliedVolumeForNonFilterEmitter),
  )

  if (!hasIrrigationAppliedVolume) return propertyLacksPermissionSectionMaker(commonReturnItems)

  const pointsWithPressure = Object.values(property?.points ?? {}).filter((point) =>
    point.valuesTimeseries.some(isAppliedVolumeForNonFilterEmitter),
  )

  const items = pointsWithPressure
    .flatMap((point) => {
      const { externalVendorNames, lngLat, name, valuesTimeseries } = point
      const valuesForPoint = valuesTimeseries.filter(isAppliedVolumeForNonFilterEmitter)

      return valuesForPoint.flatMap((valueType) => {
        const emitterType = getEnumFromValueType(valueType)

        if (!emitterType) return []

        const values = (data.points?.[lngLat]?.values?.[valueType] ?? []).reduce<
          Record<string, CommonValueType>
        >((acc, v) => {
          // @ts-expect-error
          v.timeseries.forEach(({ timestamp, value }) => {
            acc[timestamp] = { ...value, timestamp }
          })

          return acc
        }, {})

        return generateItem({
          id: `${lngLat}-${emitterType}`,
          deviceName: name,
          lngLat,
          emitterType,
          externalVendorNames,
          values,
          currentValuesData: currentValuesData?.points ?? {},
        })
      })
    })
    .sort(sortByKey('labelString'))
    .filter(Boolean)

  return {
    ...commonReturnItems,
    items,
    titleToggle: <IrrigationActivityToggleButton />,
  }
}
